<template>
  <!-- START:: PROFILE CARD -->
  <div
    class="custom_card profile_card creative_profile_card"
    :style="cardCssVars"
  >
    <router-link :to="link" class="profile_card_inner">
      <!-- Background Ambient Glow -->
      <div class="card_ambient_glow"></div>
      <div class="card_pattern_overlay"></div>

      <!-- Top Badge / Indicator (if present) -->
      <div class="card_top_badge" v-if="badge">
        <span class="badge_pill" :style="badgeStyle">{{ badge }}</span>
      </div>

      <!-- Icon Box Container with Dual Ring & Hover Lift -->
      <div class="card_icon_wrapper">
        <div class="icon_bg_box" :style="iconBgStyle">
          <span class="icon_span" :style="iconTextStyle">
            <i :class="[icon]"></i>
          </span>
        </div>
        <div class="icon_glow_ring"></div>
      </div>

      <!-- Card Text Content -->
      <div class="card_body_content">
        <h3 class="card_main_title">{{ title }}</h3>
        <p class="card_sub_title" v-if="subTitle">{{ subTitle }}</p>
      </div>

      <!-- Footer Action Prompt -->
      <div class="card_footer_action">
        <span class="action_lbl">استعراض</span>
        <span class="action_icon_arrow">
          <i class="fal fa-arrow-left"></i>
        </span>
      </div>
    </router-link>
  </div>
  <!-- END:: PROFILE CARD  -->
</template>

<script>
export default {
  name: "ProfileCard",
  props: {
    icon: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    subTitle: {
      type: String,
      default: "",
    },
    link: {
      type: String,
      required: true,
    },
    color: {
      type: String,
      default: "#6366f1",
    },
    bgColor: {
      type: String,
      default: "rgba(99, 102, 241, 0.1)",
    },
    gradient: {
      type: String,
      default: "",
    },
    badge: {
      type: [String, Number],
      default: null,
    },
  },
  computed: {
    effectiveGradient() {
      if (this.gradient) return this.gradient;
      if (this.color && this.color.includes("rgb")) {
        return `linear-gradient(135deg, ${this.color} 0%, ${this.color} 100%)`;
      }
      return `linear-gradient(135deg, ${
        this.color || "#6366f1"
      } 0%, #8b5cf6 100%)`;
    },
    cardCssVars() {
      return {
        "--card-theme-color": this.color || "#6366f1",
        "--card-theme-bg": this.bgColor || "rgba(99, 102, 241, 0.1)",
        "--card-theme-gradient": this.effectiveGradient,
      };
    },
    iconBgStyle() {
      if (this.gradient) {
        return { background: this.gradient };
      }
      return { background: this.bgColor || "rgba(99, 102, 241, 0.1)" };
    },
    iconTextStyle() {
      if (this.gradient) {
        return { color: "#ffffff" };
      }
      return { color: this.color || "#6366f1" };
    },
    badgeStyle() {
      return {
        background: this.bgColor,
        color: this.color,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.creative_profile_card {
  position: relative;
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.04),
    0 8px 10px -6px rgba(15, 23, 42, 0.02);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  height: 100%;
  z-index: 1;

  &::before,
  &::after {
    display: none !important;
  }

  .profile_card_inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 26px 20px 20px;
    text-decoration: none !important;
    height: 100%;
    position: relative;
    z-index: 2;
  }

  /* Ambient Glow Sphere in Background */
  .card_ambient_glow {
    position: absolute;
    top: -40px;
    right: -40px;
    width: 130px;
    height: 130px;
    border-radius: 50%;
    background: var(--card-theme-gradient);
    opacity: 0.07;
    filter: blur(30px);
    transition: all 0.5s ease;
    pointer-events: none;
  }

  .card_pattern_overlay {
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(148, 163, 184, 0.15) 1px,
      transparent 1px
    );
    background-size: 16px 16px;
    opacity: 0.4;
    pointer-events: none;
    transition: opacity 0.4s ease;
  }

  /* Badge */
  .card_top_badge {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    margin-bottom: 8px;

    .badge_pill {
      font-size: 0.725rem;
      font-weight: 700;
      padding: 3px 10px;
      border-radius: 50px;
      letter-spacing: 0.3px;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.04);
    }
  }

  /* Icon Section */
  .card_icon_wrapper {
    position: relative;
    margin-bottom: 18px;
    display: flex;
    align-items: center;
    justify-content: center;

    .icon_bg_box {
      width: 74px;
      height: 74px;
      border-radius: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.12);
      transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
      position: relative;
      z-index: 2;

      .icon_span {
        font-size: 1.85rem;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.4s ease;
      }
    }

    .icon_glow_ring {
      position: absolute;
      inset: -6px;
      border-radius: 26px;
      border: 2px dashed var(--card-theme-color);
      opacity: 0;
      transform: scale(0.85);
      transition: all 0.4s ease;
      z-index: 1;
    }
  }

  /* Text Section */
  .card_body_content {
    text-align: center;
    width: 100%;
    margin-bottom: 16px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;

    .card_main_title {
      font-size: 1.1rem;
      font-weight: 700;
      color: #1e293b;
      margin-bottom: 6px;
      transition: color 0.3s ease;
      line-height: 1.3;
    }

    .card_sub_title {
      font-size: 0.825rem;
      color: #64748b;
      margin: 0;
      line-height: 1.45;
      font-weight: 400;
    }
  }

  /* Action Footer */
  .card_footer_action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--card-theme-color);
    padding: 6px 16px;
    border-radius: 12px;
    background: var(--card-theme-bg);
    transition: all 0.3s ease;

    .action_icon_arrow {
      display: inline-flex;
      align-items: center;
      transition: transform 0.3s ease;
      font-size: 0.8rem;
    }
  }

  /* Hover State Effects */
  &:hover {
    transform: translateY(-8px);
    border-color: rgba(99, 102, 241, 0.4);
    box-shadow: 0 20px 35px -8px rgba(15, 23, 42, 0.1),
      0 0 25px -5px var(--card-theme-bg);

    .card_ambient_glow {
      opacity: 0.22;
      transform: scale(1.6);
    }

    .card_pattern_overlay {
      opacity: 0.8;
    }

    .card_icon_wrapper {
      .icon_bg_box {
        transform: scale(1.08) rotate(-4deg);
        box-shadow: 0 15px 25px -5px rgba(0, 0, 0, 0.22);

        .icon_span {
          transform: scale(1.1);
        }
      }

      .icon_glow_ring {
        opacity: 0.45;
        transform: scale(1);
      }
    }

    .card_body_content .card_main_title {
      color: var(--card-theme-color);
    }

    .card_footer_action {
      background: var(--card-theme-color);
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

      .action_icon_arrow {
        transform: translateX(-4px);
      }
    }
  }
}
</style>
